import "./App.css";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { LanguageProvider, useLang } from "@/lib/i18n";
// Gli indirizzi per lingua: `/it/...` e `/en/...` per le pagine pubbliche di
// marketing, con gli slug tradotti. Le rotte tecniche restano dove sono (vedi
// il docstring del modulo): un deep link di un'email non si puo' permettere un
// prefisso che nessuno ha mai scritto.
import {
  DEFAULT_URL_LOCALE, MARKETING_PAGES, URL_LOCALES, localeOf, localeOfSlug,
  marketingPath,
} from "@/lib/localePath";
import { saveLocaleToAccount, suggestLangFromEdge } from "@/lib/localeApi";
import { CurrencyProvider } from "@/lib/currency";
import { Toaster } from "@/components/ui/sonner";
// Il rientro del login Google nell'app: deep link `hostaly://auth/google`.
// Montato qui, accanto al router, e non dentro le pagine di login: il guscio
// può essere riavviato dal deep link, e in quel caso nessuna pagina di login è
// montata ad aspettarlo (vedi il componente).
import NativeAuthBridge from "@/components/NativeAuthBridge";
// Le pagine arrivano una alla volta, con un `import()` per rotta. Prima erano
// tutte importate qui in cima: il bundle iniziale conteneva tutte e 69 le
// schermate (2,7 MB di JS minificato) e il WebView doveva scaricarlo,
// compilarlo ed eseguirlo tutto prima di disegnare la prima, comprese quelle
// che dal telefono non si aprono mai (console staff, rendiconto, kit di marca).
// Il prezzo di questa scelta è un passaggio in più la prima volta che una rotta
// si apre: nel guscio i pezzi sono file locali, quindi si paga il parsing, non
// la rete.
//
// Restano subito pronte le due schermate di ingresso: `Landing` è la home del
// sito (è ciò che si dipinge per primo, e un giro in più lì si paga in
// pubblicità) e `Auth` è la prima cosa che vede chi apre l'app senza sessione.
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";

const OAuthAuthorize = lazy(() => import("@/pages/OAuthAuthorize"));
const AppleCallback = lazy(() => import("@/pages/AppleCallback"));
const Vetrina = lazy(() => import("@/pages/Vetrina"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const AccountingPage = lazy(() => import("@/pages/AccountingPage"));
const SyncPage = lazy(() => import("@/pages/SyncPage"));
// `PaymentStatus` esporta tre schermate dallo stesso modulo: un `import()`
// solo, tre componenti, altrimenti si dichiarerebbe tre volte lo stesso file.
const paymentStatus = () => import("@/pages/PaymentStatus");
const PaymentSuccess = lazy(() => paymentStatus().then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => paymentStatus().then((m) => ({ default: m.PaymentCancel })));
const PayPalReturn = lazy(() => paymentStatus().then((m) => ({ default: m.PayPalReturn })));
const Trade = lazy(() => import("@/pages/Trade"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const StaffActivation = lazy(() => import("@/pages/StaffActivation"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const GuestPortal = lazy(() => import("@/pages/GuestPortal"));
const GuestAuth = lazy(() => import("@/pages/GuestAuth"));
const GuestAuthCallback = lazy(() => import("@/pages/GuestAuthCallback"));
const GuestMagicConsume = lazy(() => import("@/pages/GuestMagicConsume"));
const GuestDashboard = lazy(() => import("@/pages/GuestDashboard"));
const GuestWelcome = lazy(() => import("@/pages/GuestWelcome"));
const GuestBookingDetail = lazy(() => import("@/pages/GuestBookingDetail"));
const TripGroupChat = lazy(() => import("@/pages/TripGroupChat"));
const Directory = lazy(() => import("@/pages/Directory"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
// La scheda che si apre da un metamotore (Trivago, TripAdvisor) sta fuori dalla
// ricerca perche' il clic non deve tornare a scegliere: deve solo prenotare.
const OfferPage = lazy(() => import("@/pages/OfferPage"));
const Rivendica = lazy(() => import("@/pages/Rivendica"));
const Struttura = lazy(() => import("@/pages/Struttura"));
const SchedeImportate = lazy(() => import("@/pages/SchedeImportate"));
const SelfCheckin = lazy(() => import("@/pages/SelfCheckin"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const TreasureHunt = lazy(() => import("@/pages/TreasureHunt"));
const WallOfBadges = lazy(() => import("@/pages/WallOfBadges"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const AffiliationRules = lazy(() => import("@/pages/AffiliationRules"));
const ChannelManager = lazy(() => import("@/pages/ChannelManager"));
// Termini pubblici (DSA artt. 14 e 16) e informativa privacy (GDPR art. 13):
// vanno letti **prima** di iscriversi, quindi restano raggiungibili da chi non
// ha un account — ma non pesano sulla prima schermata.
const Termini = lazy(() => import("@/pages/Termini"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Segnala = lazy(() => import("@/pages/Segnala"));
const Bedbank = lazy(() => import("@/pages/Bedbank"));
const Rates = lazy(() => import("@/pages/Rates"));
const Affiliates = lazy(() => import("@/pages/Affiliates"));
const LocalPartners = lazy(() => import("@/pages/LocalPartners"));
const MerchantPortal = lazy(() => import("@/pages/MerchantPortal"));
const MerchantLogin = lazy(() => import("@/pages/MerchantLogin"));
const MerchantArea = lazy(() => import("@/pages/MerchantArea"));
const MerchantAdmin = lazy(() => import("@/pages/MerchantAdmin"));
const PrizeCode = lazy(() => import("@/pages/PrizeCode"));
const BrandKit = lazy(() => import("@/pages/BrandKit"));

import { lazy, Suspense, useEffect } from "react";
import { getUser, logout } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";

/**
 * Le pagine che hanno un indirizzo per lingua, con il componente che le rende.
 *
 * Sta qui e non sparso nelle `<Route>` perche' lo stesso elenco serve a due
 * cose che devono restare d'accordo: le rotte con prefisso (`/en/pricing`) e
 * quelle vecchie senza (`/pricing`, che esistono ancora nei link e nelle email
 * e devono portare alla sezione giusta invece di dare una pagina bianca).
 */
const MARKETING_ROUTES = [
  { key: "home", Component: Landing },
  { key: "pricing", Component: Pricing },
  { key: "localPartners", Component: LocalPartners },
  { key: "merchants", Component: MerchantPortal },
  { key: "directory", Component: Directory },
  { key: "channelManager", Component: ChannelManager },
  { key: "terms", Component: () => <Termini doc="host" /> },
  { key: "guestTerms", Component: () => <Termini doc="ospite" /> },
  { key: "privacy", Component: Privacy },
  { key: "report", Component: Segnala },
];

const MARKETING_COMPONENTS = Object.fromEntries(
  MARKETING_ROUTES.map(({ key, Component }) => [key, Component]));

/**
 * Tutti gli indirizzi da cui una pagina di marketing si puo' aprire: le due
 * sezioni linguistiche e gli slug storici senza prefisso. La mappa
 * percorso → chiave tiene insieme le due forme, cosi' `/pricing` e
 * `/it/prezzi` dichiarano la stessa pagina.
 */
const MARKETING_PATHS = (() => {
  const seen = new Map();
  MARKETING_ROUTES.forEach(({ key }) => {
    URL_LOCALES.forEach((code) => {
      const path = marketingPath(key, code);
      if (path && !seen.has(path)) seen.set(path, key);
    });
    Object.values(MARKETING_PAGES[key] || {}).forEach((slug) => {
      if (slug && !seen.has(`/${slug}`)) seen.set(`/${slug}`, key);
    });
  });
  return [...seen.entries()];
})();

/**
 * Tiene lo slug e la sezione d'accordo con la lingua che l'indirizzo dichiara.
 *
 * Tre casi, e ognuno e' un indirizzo che qualcuno puo' davvero digitare:
 * `/en/prezzi` (prefisso giusto, slug italiano), `/pricing` (slug inglese
 * senza sezione) e `/prezzi` (slug italiano senza sezione). Il canonical non
 * puo' essere tre cose diverse per la stessa pagina: qui si sceglie quello
 * giusto e si arriva li' con un `replace`, senza aggiungere una voce alla
 * cronologia del browser per un errore di scrittura. In produzione il 301 lo
 * fa gia' il server; questo e' il ramo che copre il dev server e la
 * navigazione dentro l'app.
 */
function MarketingPage({ page, children }) {
  const { lang } = useLang();
  const { pathname, search, hash } = useLocation();
  const urlLang = localeOf(pathname) || localeOfSlug(pathname) || DEFAULT_URL_LOCALE;
  const expected = marketingPath(page, urlLang);
  const actual = pathname.replace(/\/+$/, "") || "/";
  if (expected && actual !== expected) {
    // Le cinque lingue senza sezione vivono su `?lang=xx`: se l'utente sta
    // leggendo in spagnolo e un link lo porta su `/prezzi`, il redirect non
    // deve riportarlo all'italiano. La lingua resta nella query, che e' l'unico
    // posto in cui puo' stare per una lingua che non ha un indirizzo.
    const params = new URLSearchParams(search);
    if (!params.get("lang") && lang && !URL_LOCALES.includes(lang)) {
      params.set("lang", lang);
    }
    const query = params.toString();
    return <Navigate to={`${expected}${query ? `?${query}` : ""}${hash}`} replace />;
  }
  return children;
}

/**
 * Quello che si vede mentre una rotta arriva.
 *
 * Sfondo del tema e non bianco: il passaggio da una schermata all'altra non
 * deve lampeggiare. Il testo è per i lettori di schermo e per le reti lente del
 * web; nel guscio dura un fotogramma, perché i pezzi sono file locali.
 */
function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Caricamento…</span>
      <span className="h-6 w-6 rounded-full border-2 border-border border-t-primary animate-spin" />
    </div>
  );
}

function App() {
  useEffect(() => {
    if (!("serviceWorker" in navigator) || window.location.protocol !== "https:") return;
    // Dev server: the worker must stay out of the way. Its cache is keyed on
    // filenames, and dev filenames are stable (bundle.js), so a cache-first hit
    // keeps serving the previous build no matter what was just edited — which
    // is how a stale calendar showed up in preview. Unregister any worker left
    // from an earlier session and drop its caches, so the next load is live.
    // The worker is harmless on live builds, but on ephemeral preview hosts the
    // dev build is a no-op (no /sw.js is published), so the registration is also
    // a no-op and the old dev worker keeps serving a stale index.js — unregister.
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
    if (window.caches) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
    }
    // Production builds can still use a worker. Keep this branch for future
    // deployment of /sw.js, but guard it so the dev/preview no-op path above
    // is never skipped.
    if (false) {
      // updateViaCache: "none" → the browser always re-fetches /sw.js from the
      // network, so a new release is picked up instead of a cached worker.
      // The extra update() forces the check on every load.
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" })
        .then((reg) => { reg.update().catch(() => {}); })
        .catch(() => {});
    }
  }, []);
  return (
    // La lingua si sceglie senza rete; queste due funzioni aggiungono il
    // suggerimento dell'edge e il salvataggio sull'account. Passarle da qui
    // tiene il dizionario indipendente dallo strato API (e dai suoi mock).
    <LanguageProvider suggestLang={suggestLangFromEdge} saveLang={saveLocaleToAccount}>
      <CurrencyProvider>
        <BrowserRouter>
          <PushNavigationBridge/>
          <NativeAuthBridge/>
          <InactivityTimeout/>
          {/* Una sola sospensione per tutte le rotte. Non si reindenta il
              blocco qui sotto per non sporcare il diff di sola spaziatura. */}
          <Suspense fallback={<RouteFallback/>}>
          <Routes>
            {/* La home ha un indirizzo per lingua: `/` porta alla sezione
                italiana, che e' l'`x-default` del sito. In produzione il 301
                lo fa il server prima di servire lo shell (vedi
                `backend/locales.py`); questo ramo copre il dev server e il
                caso in cui si arrivi qui con un `pushState`. */}
            {/* Nell'app il primo schermo è la reception, non il sito: chi tocca
                l'icona sta davanti a un ospite, non sta valutando il prodotto.
                Chi non ha sessione viene rimandato al login dalla dashboard
                stessa, quindi il giro resta quello. Sul web il ramo non cambia:
                la home resta la pagina che si indicizza e che si paga. */}
            <Route path="/" element={isNativeApp() ? <Navigate to="/dashboard" replace/> : <MarketingPage page="home"><Landing/></MarketingPage>} />
            {/* Tutte le pagine di marketing, nelle due sezioni linguistiche e
                negli slug storici senza prefisso. `MarketingPage` sceglie
                l'indirizzo canonico, qui si dichiara solo che esiste. */}
            {MARKETING_PATHS.map(([path, key]) => {
              const Component = MARKETING_COMPONENTS[key];
              return (
                <Route
                  key={path}
                  path={path}
                  element={<MarketingPage page={key}><Component/></MarketingPage>}
                />
              );
            })}
            {/* Alias storico della directory (`/directory`), che ora vive in
                `/it/strutture`: due indirizzi per la stessa pagina sono due
                pagine che competono fra loro, e il 301 e' il modo di dirlo. */}
            <Route path="/directory" element={<Navigate to={marketingPath("directory", DEFAULT_URL_LOCALE)} replace />} />
            {/* Motore di ricerca B2C: date, ospiti, disponibilità reale, mappa. */}
            <Route path="/cerca" element={<SearchPage/>} />
            <Route path="/search" element={<SearchPage/>} />
            {/* Atterraggio dai metamotori: `/offerta/<slug>` con le date nel
                link. `/offer` e' l'alias inglese, per le campagne che non
                vogliono una parola italiana nell'URL. */}
            <Route path="/offerta/:slug" element={<OfferPage/>} />
            <Route path="/offer/:slug" element={<OfferPage/>} />
            {/* Livello 2 della directory: le schede importate da OSM/Overture.
                Ogni scheda porta il viaggiatore al form di richiesta
                (/struttura/:key) e il proprietario alla rivendicazione. */}
            <Route path="/strutture/importate" element={<SchedeImportate/>} />
            {/* Link di rivendicazione: `?place=<key>&c=<codice campagna>`. Il
                passo 1 (la ricerca) resta pubblico anche senza account. */}
            <Route path="/rivendica" element={<Rivendica/>} />
            {/* Scheda pubblica di una struttura importata, con la richiesta di
                prenotazione. Il nome usa il path completo della chiave OSM
                ("node/123" o "way/456"), quindi va in un suo segmento `:key` e
                non può contenere uno "/" che il router leggerebbe come un altro
                livello: per questo la chiave viaggia percent-encoded
                (`node%2F123`) e il backend la decodifica da sola. */}
            <Route path="/struttura/:key" element={<Struttura/>} />
            <Route path="/self-checkin/:token" element={<SelfCheckin/>} />
            <Route path="/welcome/:token" element={<Welcome/>} />
            <Route path="/caccia-al-tesoro" element={<TreasureHunt/>} />
            <Route path="/caccia-al-tesoro/:huntId" element={<TreasureHunt/>} />
            <Route path="/muro-esploratori" element={<WallOfBadges/>} />
            <Route path="/wall-of-badges" element={<WallOfBadges/>} />
            <Route path="/citta/:city" element={<CityPage/>} />
            <Route path="/auth" element={<Auth/>} />
            <Route path="/oauth/authorize" element={<OAuthAuthorize/>} />
            {/* Return URL della popup "Continua con Apple": Apple vi riporta
                l'utente dopo l'autenticazione e lo script di Apple deve girare
                su questa pagina per passare la risposta alla finestra che ha
                aperto il login. */}
            <Route path="/auth/apple/callback" element={<AppleCallback/>} />
            <Route path="/p/:slug" element={<Vetrina/>} />
            <Route path="/dashboard" element={<Dashboard/>} />
            <Route path="/account" element={<AccountPage/>} />
            <Route path="/reviews" element={<ReviewsPage/>} />
            <Route path="/accounting" element={<AccountingPage/>} />
            <Route path="/sync" element={<SyncPage/>} />
            <Route path="/trade" element={<Trade/>} />
            <Route path="/bedbank" element={<Bedbank/>} />
            <Route path="/tariffe" element={<Rates/>} />
            <Route path="/affiliati" element={<Affiliates/>} />
            {/* Lo spazio dei commercianti è un accesso **suo**: un'attività
                locale non è un host, non ha una struttura e non deve passare
                dal form che chiede il CIN. La pagina pubblica è in
                `MARKETING_ROUTES` (con il suo slug inglese), qui restano
                l'accesso, l'area e la console: indirizzi tecnici, che non si
                traducono. */}
            <Route path="/commercianti/accesso" element={<MerchantLogin/>} />
            <Route path="/commercianti/area" element={<MerchantArea/>} />
            {/* Console staff: iscrizioni, accessi, richieste e listino di tutte
                le attività locali. Il guard è nel backend e nella pagina. */}
            <Route path="/commercianti/admin" element={<MerchantAdmin/>} />
            {/* La pagina che un codice premio apre: la stampa l'attività (il
                codice sta su un volantino o sul banco) e chi lo riceve legge
                che premio è e da chi ritirarlo. Pubblica e non indicizzata. */}
            <Route path="/premio/:code" element={<PrizeCode/>} />
            {/* Kit di marca: i banner «Preferisco Hostaly» per i social e la
                locandina A4. Non indicizzata (`noindex` nella pagina): serve a
                chi pubblica, non a chi cerca una struttura. */}
            <Route path="/kit-social" element={<BrandKit/>} />
            <Route path="/forgot-password" element={<ForgotPassword/>} />
            <Route path="/reset-password" element={<ResetPassword/>} />
            <Route path="/staff-activation" element={<StaffActivation/>} />
            <Route path="/regolamento-affiliazione" element={<AffiliationRules/>} />
            {/* `/termini-host` era un alias di `/termini`, e resta un
                indirizzo che qualcuno ha ricevuto: porta al documento, non a
                un 404. */}
            <Route path="/termini-host" element={<Navigate to={marketingPath("terms", DEFAULT_URL_LOCALE)} replace />} />
            <Route path="/guest/auth" element={<GuestAuth/>} />
            <Route path="/guest/auth/callback" element={<GuestAuthCallback/>} />
            <Route path="/guest/auth/magic" element={<GuestMagicConsume/>} />
            <Route path="/guest/welcome" element={<GuestWelcome/>} />
            <Route path="/guest/dashboard" element={<GuestDashboard/>} />
            <Route path="/guest/dashboard/booking/:id" element={<GuestBookingDetail/>} />
            <Route path="/trip-group-chat/:tripGroupId" element={<TripGroupChat/>} />
            <Route path="/guest/:session_id" element={<GuestPortal/>} />
            <Route path="/guest/portal/:session_id" element={<GuestPortal/>} />
            <Route path="/payment/success" element={<PaymentSuccess/>} />
            <Route path="/payment/cancel" element={<PaymentCancel/>} />
            {/* PayPal sends the guest back here after approval (?token=<order id>) */}
            <Route path="/payment/paypal/return" element={<PayPalReturn/>} />
          </Routes>
          </Suspense>
          <Toaster position="top-right"/>
        </BrowserRouter>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;

// Listens for Service Worker "hh-navigate" messages (fired when the host
// clicks a Web Push notification) and performs SPA navigation via
// react-router. Mounted inside BrowserRouter so useNavigate is available.
function InactivityTimeout() {
  const navigate = useNavigate();
  useEffect(() => {
    let timer;
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        const user = getUser();
        if (!user || user.is_superuser || user?.role === "superhost") return;
        await logout();
        navigate("/auth?mode=login&reason=timeout", { replace: true });
      }, 15 * 60 * 1000);
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((event) => window.addEventListener(event, reset, { passive: true }));
    reset();
    return () => {
      window.clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, reset));
    };
  }, [navigate]);
  return null;
}

function PushNavigationBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event) => {
      const data = event.data || {};
      if (data.type !== "hh-navigate" || !data.url) return;
      try {
        // Only handle same-origin, in-app URLs
        const parsed = new URL(data.url, window.location.origin);
        if (parsed.origin !== window.location.origin) return;
        navigate(parsed.pathname + parsed.search + parsed.hash);
      } catch { /* ignore malformed URLs */ }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
  return null;
}
